(function initializePrintPricingCalculator(global) {
  'use strict';

  if (!global) return;
  if (global.StackhousePrintPricingCalculator) {
    if (global.document) global.StackhousePrintPricingCalculator.initAll(global.document);
    return;
  }

  function parseInput(value) {
    if (value === '' || value === null || typeof value === 'undefined') return 0;
    var parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function calculateMinimumPrice(values) {
    var printPackaging = parseInput(values.printPackaging);
    var sellingFeesPercent = parseInput(values.sellingFeesPercent);
    var labor = parseInput(values.labor);
    var wastePercent = parseInput(values.wastePercent);
    var desiredProfit = parseInput(values.desiredProfit);
    var inputs = [printPackaging, sellingFeesPercent, labor, wastePercent, desiredProfit];

    if (inputs.some(function invalidNumber(value) { return value === null || value < 0; })) {
      return { status: 'invalid', message: 'Enter zero or a positive number in each field.' };
    }

    if (sellingFeesPercent >= 100) {
      return { status: 'invalid', message: 'Selling fees must be less than 100%.' };
    }

    if (inputs.every(function isZero(value) { return value === 0; })) {
      return { status: 'empty' };
    }

    var baseCost = printPackaging + labor;
    var wasteAllowance = baseCost * (wastePercent / 100);
    var costBeforeFees = baseCost + wasteAllowance;
    var minimumRetailPrice = (costBeforeFees + desiredProfit) / (1 - sellingFeesPercent / 100);
    var sellingFeeReserve = minimumRetailPrice * (sellingFeesPercent / 100);
    var marginPercent = minimumRetailPrice > 0 ? (desiredProfit / minimumRetailPrice) * 100 : 0;

    return {
      status: 'valid',
      printPackaging: printPackaging,
      sellingFeesPercent: sellingFeesPercent,
      labor: labor,
      wastePercent: wastePercent,
      desiredProfit: desiredProfit,
      baseCost: baseCost,
      wasteAllowance: wasteAllowance,
      costBeforeFees: costBeforeFees,
      sellingFeeReserve: sellingFeeReserve,
      minimumRetailPrice: minimumRetailPrice,
      marginPercent: marginPercent
    };
  }

  function formatMoney(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  function formatPercent(value) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value) + '%';
  }

  function initialize(root) {
    if (!root || root.dataset.printPricingCalculatorInitialized === 'true') return;

    var form = root.querySelector('[data-price-form]');
    if (!form) return;

    var fields = {
      printPackaging: root.querySelector('[data-price-print-packaging]'),
      sellingFeesPercent: root.querySelector('[data-price-selling-fees]'),
      labor: root.querySelector('[data-price-labor]'),
      wastePercent: root.querySelector('[data-price-waste]'),
      desiredProfit: root.querySelector('[data-price-profit]')
    };
    var output = {
      minimumRetailPrice: root.querySelector('[data-price-minimum]'),
      marginPercent: root.querySelector('[data-price-margin]'),
      baseCost: root.querySelector('[data-price-base-cost]'),
      wasteAllowance: root.querySelector('[data-price-waste-allowance]'),
      sellingFeeReserve: root.querySelector('[data-price-fee-reserve]'),
      desiredProfit: root.querySelector('[data-price-profit-output]'),
      notice: root.querySelector('[data-price-notice]')
    };

    function setOutputs(state) {
      if (state.status !== 'valid') {
        output.minimumRetailPrice.textContent = '$0.00';
        output.marginPercent.textContent = '0.0%';
        output.baseCost.textContent = '$0.00';
        output.wasteAllowance.textContent = '$0.00';
        output.sellingFeeReserve.textContent = '$0.00';
        output.desiredProfit.textContent = '$0.00';
        output.notice.textContent = state.status === 'invalid'
          ? state.message
          : 'Enter the costs for one print to calculate a minimum price.';
        root.dataset.calculatorStatus = state.status;
        delete root.dataset.minimumPrice;
        return;
      }

      output.minimumRetailPrice.textContent = formatMoney(state.minimumRetailPrice);
      output.marginPercent.textContent = formatPercent(state.marginPercent);
      output.baseCost.textContent = formatMoney(state.baseCost);
      output.wasteAllowance.textContent = formatMoney(state.wasteAllowance);
      output.sellingFeeReserve.textContent = formatMoney(state.sellingFeeReserve);
      output.desiredProfit.textContent = formatMoney(state.desiredProfit);
      output.notice.textContent = 'This is the lowest price that covers the costs and profit entered above.';
      root.dataset.calculatorStatus = 'valid';
      root.dataset.minimumPrice = state.minimumRetailPrice.toFixed(2);
    }

    function render() {
      setOutputs(calculateMinimumPrice({
        printPackaging: fields.printPackaging.value,
        sellingFeesPercent: fields.sellingFeesPercent.value,
        labor: fields.labor.value,
        wastePercent: fields.wastePercent.value,
        desiredProfit: fields.desiredProfit.value
      }));
    }

    form.addEventListener('input', render);
    form.addEventListener('change', render);
    form.addEventListener('reset', function handleReset() {
      global.setTimeout(render, 0);
    });

    root.dataset.printPricingCalculatorInitialized = 'true';
    render();
  }

  function initAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches('[data-print-pricing-calculator]')) initialize(scope);
    scope.querySelectorAll('[data-print-pricing-calculator]').forEach(initialize);
  }

  var api = {
    calculateMinimumPrice: calculateMinimumPrice,
    formatMoney: formatMoney,
    formatPercent: formatPercent,
    initAll: initAll
  };

  global.StackhousePrintPricingCalculator = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function onReady() {
        initAll(global.document);
      }, { once: true });
    } else {
      initAll(global.document);
    }

    global.document.addEventListener('shopify:section:load', function onSectionLoad(event) {
      initAll(event.target);
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
